import React, { useEffect, useMemo, useState } from 'react';
import { getDisplayImageUrl } from '../../utils/imageUtils';

const defaultSlides = [
  {
    id: 1,
    title: "Nueva colección",
    subtitle: "Indumentaria urbana y deportiva",
    cta: "DESCUBRIR",
    backgroundImage: "/images/slider/slide1.png"
  },
  {
    id: 2,
    title: "Estilo urbano para todos",
    subtitle: "Mujer, hombre y niños",
    cta: "VER COLECCIÓN",
    backgroundImage: "/images/slider/slide2.png"
  },
  {
    id: 3,
    title: "Fabricación propia",
    subtitle: "Hecha en Uruguay",
    cta: "VER COLECCIÓN",
    backgroundImage: "/images/slider/slide3.png"
  },
  {
    id: 4,
    title: "Rem Urban Clothes",
    subtitle: "En cada ocasión",
    cta: "EXPLORAR",
    backgroundImage: "/images/slider/slide4.png"
  }
];

const InicioSlider = ({ slides: incomingSlides, enabled = true }) => {
  const slides = useMemo(() => {
    const sourceSlides = Array.isArray(incomingSlides) && incomingSlides.length > 0
      ? incomingSlides
      : defaultSlides;

    return sourceSlides.map((slide) => ({
      ...slide,
      cta: String(slide.cta || '').trim().toUpperCase() === 'SHOP NOW' ? 'VER COLECCIÓN' : slide.cta,
      subtitle: String(slide.subtitle || '').replace(/\bocacion\b/gi, 'ocasión')
    }));
  }, [incomingSlides]);

  const [slideIndex, setSlideIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (slideIndex >= slides.length) {
      setSlideIndex(0);
    }
  }, [slideIndex, slides.length]);

  const changeSlide = (newIndex) => {
    if (isAnimating) return;
    setIsAnimating(true);
    let targetIndex = newIndex;
    if (targetIndex < 0) targetIndex = slides.length - 1;
    if (targetIndex >= slides.length) targetIndex = 0;
    setSlideIndex(targetIndex);
    setTimeout(() => setIsAnimating(false), 800);
  };

  useEffect(() => {
    const slideTimer = setInterval(() => {
      changeSlide(slideIndex + 1);
    }, 6000);
    return () => clearInterval(slideTimer);
  }, [slideIndex, isAnimating]);

  if (!enabled || slides.length === 0) {
    return null;
  }

  return (
    <div style={{
      position: 'relative',
      width: '100%',
      height: 'calc(100vh - 100px)',
      minHeight: '500px',
      maxHeight: '900px',
      overflow: 'hidden',
      backgroundColor: 'var(--bg-secondary)'
    }}>
      {/* Slides */}
      <div style={{
        display: 'flex',
        width: `${slides.length * 100}%`,
        height: '100%',
        transform: `translateX(-${slideIndex * (100 / slides.length)}%)`,
        transition: 'transform 0.8s cubic-bezier(0.645, 0.045, 0.355, 1)'
      }}>
        {slides.map((slide, index) => (
          <div
            key={slide._id || slide.id || `slide-${index}`}
            style={{
              width: `${100 / slides.length}%`,
              height: '100%',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Background Image */}
            <div
              className={index === slideIndex ? "slide-bg-anim" : ""}
              style={{
                position: 'absolute',
                top: 0, left: 0,
                width: '100%', height: '100%',
                backgroundImage: `url(${getDisplayImageUrl(slide.backgroundImage)})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                willChange: 'transform'
              }}
            />

            {/* Overlay */}
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              background: 'linear-gradient(to right, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.1) 60%, transparent 100%)',
              zIndex: 1
            }} />

            {/* Content — Left Aligned */}
            <div style={{
              position: 'absolute',
              bottom: '15%',
              left: 'clamp(30px, 8vw, 100px)',
              zIndex: 2,
              maxWidth: '500px'
            }}>
              <p
                className={index === slideIndex ? "slide-content-anim" : ""}
                style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  letterSpacing: '0.3em',
                  color: 'rgba(255,255,255,0.8)',
                  marginBottom: '12px'
                }}
              >
                {slide.subtitle}
              </p>

              <h2
                className={index === slideIndex ? "slide-content-anim slide-content-anim-delay-1" : ""}
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontSize: 'clamp(2.5rem, 7vw, 4.5rem)',
                  fontWeight: 300,
                  color: '#ffffff',
                  lineHeight: 1.05,
                  marginBottom: '28px',
                  letterSpacing: '0.03em'
                }}
              >
                {slide.title}
              </h2>

              <a
                href={slide.link || '/tienda'}
                className={index === slideIndex ? "slide-content-anim slide-content-anim-delay-2" : ""}
                style={{
                  display: 'inline-block',
                  padding: '14px 40px',
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.7)',
                  color: '#ffffff',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  textDecoration: 'none',
                  fontFamily: 'var(--font-body)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={e => {
                  e.target.style.background = '#ffffff';
                  e.target.style.color = '#1a1a1a';
                }}
                onMouseLeave={e => {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#ffffff';
                }}
              >
                {slide.cta || 'EXPLORAR'}
              </a>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      <div style={{
        position: 'absolute',
        bottom: '40px',
        right: 'clamp(30px, 8vw, 100px)',
        display: 'flex',
        gap: '8px',
        zIndex: 10
      }}>
        <button
          onClick={() => changeSlide(slideIndex - 1)}
          style={{
            width: '44px', height: '44px',
            background: 'transparent',
            border: '1px solid rgba(255,255,255,0.4)',
            color: 'white',
            cursor: 'pointer',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        >
          <i className="fa-solid fa-arrow-left"></i>
        </button>
        <button
          onClick={() => changeSlide(slideIndex + 1)}
          style={{
            width: '44px', height: '44px',
            background: 'transparent',
            border: '1px solid rgba(255,255,255,0.4)',
            color: 'white',
            cursor: 'pointer',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        >
          <i className="fa-solid fa-arrow-right"></i>
        </button>
      </div>

      {/* Progress Indicator */}
      <div style={{
        position: 'absolute',
        bottom: '40px',
        left: 'clamp(30px, 8vw, 100px)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        zIndex: 10
      }}>
        <span style={{
          color: 'rgba(255,255,255,0.7)',
          fontSize: '0.7rem',
          fontWeight: 500,
          letterSpacing: '0.1em',
          fontFamily: 'var(--font-body)'
        }}>
          {String(slideIndex + 1).padStart(2, '0')}
        </span>
        <div style={{
          width: '60px', height: '1px',
          background: 'rgba(255,255,255,0.3)',
          position: 'relative'
        }}>
          <div style={{
            position: 'absolute', top: 0, left: 0,
            height: '100%',
            width: `${((slideIndex + 1) / slides.length) * 100}%`,
            background: '#ffffff',
            transition: 'width 0.5s ease'
          }} />
        </div>
        <span style={{
          color: 'rgba(255,255,255,0.4)',
          fontSize: '0.7rem',
          fontWeight: 500,
          letterSpacing: '0.1em',
          fontFamily: 'var(--font-body)'
        }}>
          {String(slides.length).padStart(2, '0')}
        </span>
      </div>
    </div>
  );
};

export default InicioSlider;
